import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { MaskService } from '../../../../../shared/services/mask.service';

export interface AjustarTemposEtapaDialogData {
  descricaoEtapa: string;
  datetimeInicio: Date | null;
  datetimeFim: Date | null;
}

export interface AjustarTemposEtapaDialogResult {
  datetimeInicio: Date | null;
  datetimeFim: Date | null;
}

/**
 * Ajuste manual dos tempos de uma etapa (seção 8.6 da US#141200). Data e hora em campos separados
 * — é o único padrão de data+hora que existe hoje no sistema (perfil-cadastro); datetime-local não
 * aparece em nenhuma tela.
 *
 * O diálogo só devolve os dois DateTime combinados; quem chama a API e trata erro/refresh é a tela
 * de Detalhe, mesmo padrão de alterar-tags-ordem-producao-dialog.
 */
@Component({
  selector: 'app-ajustar-tempos-etapa-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDatepickerModule,
  ],
  templateUrl: './ajustar-tempos-etapa-dialog.component.html',
  styleUrl: './ajustar-tempos-etapa-dialog.component.css',
})
export class AjustarTemposEtapaDialogComponent {
  public dataInicio: Date | null;
  public horaInicio: string | null;
  public dataFim: Date | null;
  public horaFim: string | null;

  public mensagemErro: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<AjustarTemposEtapaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AjustarTemposEtapaDialogData,
  ) {
    this.dataInicio = data.datetimeInicio ? new Date(data.datetimeInicio) : null;
    this.horaInicio = data.datetimeInicio ? this.formatarHora(new Date(data.datetimeInicio)) : null;
    this.dataFim = data.datetimeFim ? new Date(data.datetimeFim) : null;
    this.horaFim = data.datetimeFim ? this.formatarHora(new Date(data.datetimeFim)) : null;
  }

  public onDateInput(event: Event, campo: 'dataInicio' | 'dataFim'): void {
    const control = { setValue: (v: Date) => (this[campo] = v), markAsTouched: () => {} } as any;
    MaskService.onDateInput(event, control);
  }

  private formatarHora(data: Date): string {
    return `${data.getHours().toString().padStart(2, '0')}:${data.getMinutes().toString().padStart(2, '0')}`;
  }

  private combinar(data: Date | null, hora: string | null): Date | null {
    if (!data) return null;
    const [h, m] = (hora || '00:00').split(':').map(Number);
    const combinado = new Date(data);
    combinado.setHours(h || 0, m || 0, 0, 0);
    return combinado;
  }

  public get inicioCombinado(): Date | null {
    return this.combinar(this.dataInicio, this.horaInicio);
  }

  public get fimCombinado(): Date | null {
    return this.combinar(this.dataFim, this.horaFim);
  }

  /** Texto "25min"/"1h 30min" — só quando os dois momentos estão preenchidos. */
  public get duracaoCalculada(): string | null {
    const inicio = this.inicioCombinado;
    const fim = this.fimCombinado;
    if (!inicio || !fim) return null;

    const minutosTotais = Math.round((fim.getTime() - inicio.getTime()) / 60000);
    if (minutosTotais < 0) return null;

    const horas = Math.floor(minutosTotais / 60);
    const minutos = minutosTotais % 60;
    if (horas === 0) return `${minutos}min`;
    if (minutos === 0) return `${horas}h`;
    return `${horas}h ${minutos}min`;
  }

  public salvar(): void {
    const inicio = this.inicioCombinado;
    const fim = this.fimCombinado;

    if (inicio && fim && fim < inicio) {
      this.mensagemErro = 'O término não pode ser anterior ao início.';
      return;
    }

    this.mensagemErro = null;
    this.dialogRef.close({ datetimeInicio: inicio, datetimeFim: fim } as AjustarTemposEtapaDialogResult);
  }
}
